import React, { useCallback } from 'react';
import { LuFilter } from 'react-icons/lu';
import { useDispatch, useSelector } from 'react-redux';

import LinkButton from 'components/common/LinkButton/LinkButton';
import {
  INPUT_DOC_TAB_PLANNING,
  LIVE_TOTALS_FILTERED_LABEL,
  LIVE_TOTALS_SHOW_WHOLE_PLAN_LABEL,
  PLANNING_LENS_PADDOCK_PLAN,
} from 'constants/uiConstants';
import { getStepCategoryFilter } from 'pages/FarmsPage/Workspace/Tasks/stepCategoryFilters';

import { setTasksStepCategoryKey } from '../../../redux/slices/InputSlice';
import { RootState } from '../../../redux/Store';

/**
 * The tasks grid's input filter as Live totals see it (OP-2535): the active
 * filter key and label while the grid is on screen, otherwise nulls, and a
 * way to clear it.
 */
export const useGridStepFilter = () => {
  const dispatch = useDispatch();
  const tasksStepCategoryKey = useSelector(
    (state: RootState) => state.inputReducer.tasksStepCategoryKey
  );
  const isTasksGridOpen = useSelector(
    (state: RootState) => state.inputReducer.isTasksGridOpen
  );
  // The Planning tab stays mounted (hidden) behind other tabs, so the grid
  // being mounted isn't enough — it must be the view on screen.
  const isGridOnScreen = useSelector(
    (state: RootState) =>
      state.insightsReducer.activeTab === INPUT_DOC_TAB_PLANNING &&
      (state.insightsReducer.planningLens === PLANNING_LENS_PADDOCK_PLAN ||
        state.insightsReducer.planningLens === null)
  );
  const filter =
    isTasksGridOpen && isGridOnScreen && tasksStepCategoryKey
      ? getStepCategoryFilter(tasksStepCategoryKey)
      : undefined;
  const clear = useCallback(
    () => dispatch(setTasksStepCategoryKey(null)),
    [dispatch]
  );
  return {
    categoryKey: filter ? filter.key : null,
    label: filter?.label ?? null,
    clear,
  };
};

interface GridFilterNoteProps {
  label: string;
  onClear: () => void;
}

/** Says a Live totals section is filtered, so it's never read as the whole plan. */
const GridFilterNote: React.FC<GridFilterNoteProps> = ({ label, onClear }) => (
  <div
    role="status"
    className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-[var(--radius-button)] bg-secondary-light px-2 py-1.5 text-sm text-charcoal"
  >
    <span className="inline-flex items-center gap-1.5">
      <LuFilter aria-hidden="true" className="shrink-0 text-gray-dark" />
      {LIVE_TOTALS_FILTERED_LABEL(label)}
    </span>
    <LinkButton onClick={onClear}>{LIVE_TOTALS_SHOW_WHOLE_PLAN_LABEL}</LinkButton>
  </div>
);

export default GridFilterNote;
